import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@rb-serve-app/core';
import { useNavigate } from 'react-router-dom';

interface DescriptionItem {
  headerText: string;
  description: string;
}

interface ContentItem {
  image: string;
  title: string;
  descriptionContent: Array<{
    description: DescriptionItem[];
  }>;
}

interface MainContentItem {
  [key: string]: ContentItem;
}

const WelcomeContent = () => {
  const navigate = useNavigate();
  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries, assets } = contentfulData || {};
  // Try different possible paths for rbWelcomePage
  console.log('entries', entries);
  const rbWelcomePage = entries?.rb_announcement?.rbWelcomePage;
  console.log('rbWelcomePage', rbWelcomePage);
  const welcomeScreenContent = rbWelcomePage?.welcomeScreenContent;

  if (!rbWelcomePage?.welcomePageEnabled || !welcomeScreenContent) {
    // Redirect to home if welcome page is not enabled
    navigate('/home');
    return null;
  }

  const { header, description, mainContent } = welcomeScreenContent;

  const renderMainContent = () => {
    if (!mainContent || !Array.isArray(mainContent)) return null;

    return mainContent.map((contentItem: MainContentItem, index: number) => {
      const key = Object.keys(contentItem)[0];
      const content = contentItem[key];

      return (
        <section
          key={index}
          // Divider styling: adds bottom border between sections, removes border and padding from last section
          className="mb-12 pb-6 border-b border-gray-100 last:border-b-0 last:pb-0"
          aria-labelledby={`section-title-${index}`}
        >
          {/* Left-aligned title above image */}
          <h3
            id={`section-title-${index}`}
            className="text-[20px] font-bold text-black mb-6 text-left"
          >
            {content.title}
          </h3>

          {/* Full-width image with padding */}
          {content.image && assets[content.image] && (
            <div className="w-full px-4 mb-8">
              <img
                src={assets[content.image]}
                alt={`${content.title}`}
                className="w-full h-auto object-cover rounded-lg"
                role="img"
              />
            </div>
          )}

          {/* Description content in a single styled box */}
          <div
            className="rounded-[10px] border border-blue-200 bg-blue-50 p-6 mt-4"
            role="region"
            aria-label={`${content.title}`}
          >
            {content.descriptionContent.map((descContent, descIndex) => (
              <div key={descIndex}>
                {descContent.description.map((item, itemIndex) => (
                  <div key={itemIndex} className={itemIndex > 0 ? 'mt-6' : ''}>
                    <h4 className="font-semibold text-[16px] text-black mb-3 text-left">
                      {item.headerText}
                    </h4>
                    <p className="text-[14px] text-gray-700 leading-relaxed text-left mb-2">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      );
    });
  };

  return (
    <div className="bg-bg-body py-4 md:px-8 px-4">
      {/* Breadcrumbs - Outside white container */}
      <nav className="mb-8" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <button
              onClick={() => navigate('/home')}
              className="text-blue-600 hover:text-blue-800 cursor-pointer text-[14px]"
              aria-label={`${entries?.rb_navigation?.homeLogoTitle || 'Home'}`}
            >
              {entries?.rb_navigation?.homeLogoTitle || 'Home'}
            </button>
          </li>
          <li>
            <span className="text-gray-400 text-[14px]" aria-hidden="true">
              {' '}
              &gt;{' '}
            </span>
          </li>
          <li>
            <span className="text-gray-600 text-[14px]" aria-current="page">
              {rbWelcomePage?.welcomeScreenBreadCrumbTitle || "What's New?"}
            </span>
          </li>
        </ol>
      </nav>

      {/* White Container */}
      <div
        className="mx-[4px] sm:mx-6 md:mx-8 lg:mx-12 my-[22px] rounded-[15px] bg-white min-h-[70vh] overflow-y-auto scrollbar-none"
        aria-live="polite"
      >
        <div className="bg-white md:px-8 md:py-8 px-6 py-6 rounded-lg">
          {/* Header Section - Left aligned */}
          <header className="mb-12">
            <h1
              className="text-[28px] font-medium text-black mb-6 text-left leading-[34px] tracking-[0.101px] text-[#000]"
              id="welcome-content-title"
            >
              {header}
            </h1>
            <p
              className="text-[16px] font-medium text-[#1F1F1F] text-left leading-[20px] tracking-[-0.051px]"
              aria-describedby="welcome-content-title"
            >
              {description}
            </p>
          </header>

          {/* Main Content Sections */}
          <main
            className="space-y-8"
            role="main"
            aria-label="Welcome content sections"
          >
            {renderMainContent()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default WelcomeContent;
